import { Injectable } from '@angular/core';
import { Socket } from 'ngx-socket-io';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/auth/services/auth.service';

/**
 * Wraps a page-scoped ngx-socket-io connection for the Transport Timeline's presence + "changes
 * available" feature. Deliberately not the app-wide SocketIoModule.forRoot() pattern (still
 * commented out in app.module.ts) - the auth token is only known after login, and this is only
 * needed on this one page, not the whole app, so the Socket is constructed directly here rather
 * than injected from a module-level singleton.
 */
@Injectable({
  providedIn: 'root'
})
export class TimelineTransportSocketService {

  private socket: Socket = null;

  // Remembered so a reconnect (backend restart, network blip, laptop sleep - socket.io-client
  // reconnects automatically by default) can re-join the same room. Without this, a reconnected
  // socket comes back with no room membership at all: presence still looks fine once something
  // else triggers a fresh join, but this connection silently never receives notifyChanged again,
  // with no error or symptom pointing at why - it just goes quiet.
  private lastJoinedDate: string = null;
  private lastJoinedOrderTypeId: number = null;

  constructor(private authService: AuthService) { }

  connect() {
    if (this.socket) {
      return;
    }

    this.socket = new Socket({
      url: environment.api.endpoint,
      options: {
        // This app is on socket.io-client 2.x (per package.json) - handshake.auth is a v3+
        // addition and isn't available here; v2's equivalent for connection-time data is the
        // query string, matched by TransportTimelineGateway reading handshake.query.token.
        query: {
          token: this.authService.getToken(),
        },
      },
    });

    this.socket.on('connect', () => {
      // Re-join whatever room was last requested - covers both the very first connect (where
      // joinRoom() is about to be called right after connect() by the caller anyway, so this is
      // a harmless no-op since lastJoinedDate is still null) and every reconnect after that
      // (where nothing else would otherwise re-request the join).
      if (this.lastJoinedDate !== null && this.lastJoinedOrderTypeId !== null) {
        this.socket.emit('joinTimelineRoom', { date: this.lastJoinedDate, orderTypeId: this.lastJoinedOrderTypeId });
      }
    });
  }

  disconnect() {
    if (!this.socket) {
      return;
    }

    this.socket.disconnect();
    this.socket = null;
    this.lastJoinedDate = null;
    this.lastJoinedOrderTypeId = null;
  }

  joinRoom(date: string, orderTypeId: number) {
    this.lastJoinedDate = date;
    this.lastJoinedOrderTypeId = orderTypeId;

    if (!this.socket) {
      return;
    }

    this.socket.emit('joinTimelineRoom', { date, orderTypeId });
  }

  get presence$(): Observable<any[]> {
    return this.socket.fromEvent<any[]>('presence');
  }

  get changed$(): Observable<any> {
    return this.socket.fromEvent<any>('timelineChanged');
  }

  sendChatMessage(text: string) {
    if (!this.socket) {
      return;
    }

    this.socket.emit('chatMessage', { text });
  }

  get chatMessage$(): Observable<any> {
    return this.socket.fromEvent<any>('chatMessage');
  }

  get chatHistory$(): Observable<any[]> {
    return this.socket.fromEvent<any[]>('chatHistory');
  }
}
